import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { Check } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export const Differentiator = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const { t } = useLanguage();

  return (
    <section 
      data-testid="differentiator-section"
      ref={ref}
      className="py-24 md:py-32 lg:py-40 bg-[#242328] relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#ff00c7]/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#ff00c7]/5 rounded-full blur-3xl" />
      </div>

      <div className="container-main relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div>
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-xs md:text-sm font-medium uppercase tracking-widest text-[#ff00c7] mb-4 block"
            >
              {t.differentiator.badge}
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="differentiator-title"
              className="font-['Syne'] text-3xl md:text-4xl lg:text-5xl font-bold text-[#f8f6e2] leading-tight mb-6"
            >
              {t.differentiator.title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-[#f8f6e2]/70 leading-relaxed mb-8"
            >
              {t.differentiator.description}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="flex items-center gap-3"
            >
              <div className="flex -space-x-3">
                {[1, 2, 3].map((i) => (
                  <div
                    key={i}
                    className="w-10 h-10 rounded-full bg-[#eaddcf] border-2 border-[#242328] flex items-center justify-center"
                  >
                    <span className="text-xs font-semibold text-[#242328]">{i}</span>
                  </div>
                ))}
              </div>
              <p className="text-sm text-[#f8f6e2]/60">
                {t.differentiator.grewWithUs}
              </p>
            </motion.div>
          </div>

          {/* Right - Values */}
          <div className="space-y-6">
            {t.differentiator.values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, x: 40 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                data-testid={`value-${index}`}
                className="group flex gap-4 p-6 rounded-xl bg-[#f8f6e2]/5 border border-[#f8f6e2]/10 hover:border-[#ff00c7]/50 transition-colors duration-300"
              >
                <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#ff00c7] flex items-center justify-center">
                  <Check className="w-4 h-4 text-[#f8f6e2]" strokeWidth={2} />
                </div>
                <div>
                  <h3 className="font-['Syne'] text-lg font-semibold text-[#f8f6e2] mb-2 group-hover:text-[#ff00c7] transition-colors duration-300">
                    {value.title}
                  </h3>
                  <p className="text-sm text-[#f8f6e2]/60 leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
